import React, { useEffect, useRef } from 'react';

type Props = {
  value: number;
  maxValue: number;
  size: number;
  thickness?: number;
  fillColor?: string;
  bgColor?: string;
};

const PieChart: React.FC<Props> = ({
  value,
  maxValue,
  size,
  thickness = 15,
  fillColor = '#F96C75',
  bgColor = '#3BB0FF26',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const center = size / 2;
    const radius = center - thickness / 2;

    ctx.clearRect(0, 0, size, size);

    // Background ring
    ctx.beginPath();
    ctx.lineWidth = thickness;
    ctx.strokeStyle = bgColor;
    ctx.lineCap = 'butt'; // no rounding here
    ctx.arc(center, center, radius, 0, 2 * Math.PI);
    ctx.stroke();

    const safeValue = Math.min(value, maxValue);
    let progress = maxValue === 0 ? 0 : safeValue / maxValue;

    if (progress > 0.9 && progress !== 1) {
      progress *= 0.97;
    }

    if (progress > 0) {
    // Draw main arc with no rounded caps
      ctx.beginPath();
      ctx.strokeStyle = fillColor;
      ctx.lineWidth = thickness;
      ctx.lineCap = 'butt';
      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + progress * 2 * Math.PI;
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.stroke();

      // Draw a rounded cap at the start of the arc
      const startX = center + radius * Math.cos(startAngle);
      const startY = center + radius * Math.sin(startAngle);
      ctx.beginPath();
      ctx.fillStyle = fillColor;
      ctx.arc(startX, startY, thickness / 2, 0, 2 * Math.PI);
      ctx.fill();

      // Optionally, add a rounded cap at the end of the arc
      const endX = center + radius * Math.cos(endAngle);
      const endY = center + radius * Math.sin(endAngle);
      ctx.beginPath();
      ctx.fillStyle = fillColor;
      ctx.arc(endX, endY, thickness / 2, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [value, maxValue, size, thickness, fillColor, bgColor]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
};

export default PieChart;
